import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, TrendingUp, MousePointerClick, Eye, Target, DollarSign, Play, Pause, Trash2, Copy, Pencil, ChevronDown, ChevronUp, X } from "lucide-react";
import AdsWizard from "@/components/AdsWizard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRL = (n) => Number(n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const OBJ_LABEL = {
  awareness: "Reconhecimento", traffic: "Tráfego", engagement: "Engajamento",
  leads: "Leads", app_install: "App", sales: "Vendas",
};
const STATUS_LABEL = {
  draft: { label: "Rascunho", color: "bg-slate-200 text-slate-800" },
  active: { label: "Ativa", color: "bg-emerald-500 text-white" },
  paused: { label: "Pausada", color: "bg-amber-500 text-white" },
  ended: { label: "Encerrada", color: "bg-slate-400 text-white" },
};

export default function AdsManager() {
  const [dashboard, setDashboard] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardEditingId, setWizardEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([
        axios.get(`${API}/ads2/dashboard`),
        axios.get(`${API}/ads2/campaigns`),
      ]);
      setDashboard(d.data);
      setCampaigns(c.data || []);
    } catch {
      toast.error("Não foi possível carregar o Ads Manager");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (campaign_id, status) => {
    try {
      await axios.patch(`${API}/ads2/campaigns/${campaign_id}/status`, { status });
      toast.success(`Campanha ${STATUS_LABEL[status].label.toLowerCase()}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao atualizar status");
    }
  };

  const deleteCampaign = async (campaign_id) => {
    if (!window.confirm("Excluir esta campanha e todos seus conjuntos/anúncios?")) return;
    try {
      await axios.delete(`${API}/ads2/campaigns/${campaign_id}`);
      toast.success("Campanha excluída");
      load();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const duplicate = async (campaign_id) => {
    try {
      const r = await axios.post(`${API}/ads2/campaigns/${campaign_id}/duplicate`);
      toast.success(`Copiada (${r.data.cloned_ad_sets} conjuntos, ${r.data.cloned_ads} anúncios)`);
      load();
    } catch {
      toast.error("Erro ao duplicar");
    }
  };

  const toggleExpand = async (campaign_id) => {
    if (expandedId === campaign_id) {
      setExpandedId(null); setAnalytics(null); return;
    }
    setExpandedId(campaign_id);
    setAnalyticsLoading(true);
    try {
      const r = await axios.get(`${API}/ads2/campaigns/${campaign_id}/analytics?days=7`);
      setAnalytics(r.data);
    } catch {
      setAnalytics(null); toast.error("Erro ao carregar analytics");
    }
    setAnalyticsLoading(false);
  };

  const openEdit = (c) => setEditing({
    campaign_id: c.campaign_id, name: c.name, objective: c.objective,
    cbo_enabled: !!c.cbo_enabled, cbo_daily_budget: c.cbo_daily_budget || "", notes: c.notes || "",
  });
  const saveEdit = async () => {
    try {
      await axios.patch(`${API}/ads2/campaigns/${editing.campaign_id}`, {
        name: editing.name, objective: editing.objective,
        cbo_enabled: editing.cbo_enabled,
        cbo_daily_budget: editing.cbo_enabled ? Number(editing.cbo_daily_budget || 0) : null,
        notes: editing.notes,
      });
      toast.success("Campanha atualizada");
      setEditing(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    }
  };

  const openAddSet = (campaign_id) => { setWizardEditingId(campaign_id); setWizardOpen(true); };
  const openNewCampaign = () => { setWizardEditingId(null); setWizardOpen(true); };

  const k = dashboard?.kpis || {};

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6" data-testid="ads-manager-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight">Ads Manager</h1>
          <p className="text-muted-foreground mt-1 max-w-2xl">
            O leilão só organiza a entrega. O preço tem teto e <b>não fica caro</b> como nas outras redes.
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" data-testid="link-pixels">
            <Link to="/ads-manager/pixels"><Target className="w-4 h-4 mr-2" /> Pixels</Link>
          </Button>
          <Button onClick={openNewCampaign} data-testid="new-campaign-btn">
            <Plus className="w-4 h-4 mr-2" /> Nova campanha
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Campanhas" value={k.campaigns ?? 0} icon={<Target className="w-4 h-4" />} />
        <KpiCard label="Investido" value={BRL(k.spent_brl)} icon={<DollarSign className="w-4 h-4" />} accent />
        <KpiCard label="Impressões" value={k.impressions ?? 0} icon={<Eye className="w-4 h-4" />} />
        <KpiCard label="Cliques" value={k.clicks ?? 0} icon={<MousePointerClick className="w-4 h-4" />} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="CTR" value={`${(k.ctr ?? 0).toLocaleString("pt-BR")}%`} icon={<TrendingUp className="w-4 h-4" />} small />
        <KpiCard label="CPC" value={BRL(k.cpc)} icon={<DollarSign className="w-4 h-4" />} small />
        <KpiCard label="CPA" value={BRL(k.cpa)} icon={<DollarSign className="w-4 h-4" />} small />
        <KpiCard label="Conversões" value={k.conversions ?? 0} icon={<Target className="w-4 h-4" />} small />
      </div>

      {/* Expanded analytics panel */}
      {expandedId && (
        <Card className="border-primary/40" data-testid="analytics-panel">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Resultados — {campaigns.find((c) => c.campaign_id === expandedId)?.name}</CardTitle>
            <Button variant="ghost" size="icon" onClick={() => { setExpandedId(null); setAnalytics(null); }} data-testid="close-analytics">
              <X className="w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {analyticsLoading ? <p className="text-sm text-muted-foreground py-8 text-center">Carregando resultados...</p>
              : !analytics ? <p className="text-sm text-muted-foreground py-6 text-center">Sem dados no período.</p>
              : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <KpiCard label="Impressões" value={analytics.totals.impressions} icon={<Eye className="w-4 h-4" />} small />
                    <KpiCard label="Cliques" value={analytics.totals.clicks} icon={<MousePointerClick className="w-4 h-4" />} small />
                    <KpiCard label="Conversões" value={analytics.totals.conversions} icon={<Target className="w-4 h-4" />} small />
                    <KpiCard label="Investido" value={BRL(analytics.totals.spent_brl)} icon={<DollarSign className="w-4 h-4" />} accent small />
                    <KpiCard label="CTR" value={`${analytics.totals.ctr}%`} icon={<TrendingUp className="w-4 h-4" />} small />
                    <KpiCard label="CPC" value={BRL(analytics.totals.cpc)} icon={<DollarSign className="w-4 h-4" />} small />
                    <KpiCard label="CPA" value={BRL(analytics.totals.cpa)} icon={<DollarSign className="w-4 h-4" />} small />
                    <KpiCard label="Estrutura" value={`${analytics.totals.ad_sets} conjs · ${analytics.totals.ads} anún.`} icon={<Target className="w-4 h-4" />} small />
                  </div>

                  <MiniChart data={analytics.timeseries} />

                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => openAddSet(expandedId)} data-testid="add-adset-btn">
                      <Plus className="w-4 h-4 mr-1" /> Adicionar conjunto/anúncio
                    </Button>
                  </div>
                </div>
              )}
          </CardContent>
        </Card>
      )}

      {/* Campaigns list */}
      <Card>
        <CardHeader>
          <CardTitle>Suas campanhas</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Carregando...</p>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-12" data-testid="empty-campaigns">
              <Target className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <p className="font-semibold">Nenhuma campanha ainda</p>
              <p className="text-sm text-muted-foreground mb-4">Crie sua primeira campanha em 4 passos.</p>
              <Button onClick={openNewCampaign} data-testid="new-campaign-empty-btn">
                <Plus className="w-4 h-4 mr-2" /> Criar campanha
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {campaigns.map((c) => {
                const status = STATUS_LABEL[c.status] || STATUS_LABEL.draft;
                const isExpanded = expandedId === c.campaign_id;
                return (
                  <div key={c.campaign_id} className="py-3" data-testid={`campaign-row-${c.campaign_id}`}>
                    <div className="flex flex-col md:flex-row md:items-center gap-3">
                      <button onClick={() => toggleExpand(c.campaign_id)}
                        className="flex-1 min-w-0 text-left hover:bg-accent/40 rounded p-1 -m-1 transition"
                        data-testid={`campaign-expand-${c.campaign_id}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          <span className="font-semibold truncate">{c.name}</span>
                          <Badge className={status.color}>{status.label}</Badge>
                          <Badge variant="outline">{OBJ_LABEL[c.objective]}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 ml-6">
                          Investido: {BRL(c.spent_brl)}
                          {c.cbo_enabled && c.cbo_daily_budget && <> · CBO: {BRL(c.cbo_daily_budget)}/dia</>}
                        </p>
                      </button>
                      <div className="flex gap-1 flex-wrap">
                        {c.status !== "active" && (
                          <Button size="sm" variant="outline" onClick={() => updateStatus(c.campaign_id, "active")} data-testid={`activate-${c.campaign_id}`}>
                            <Play className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {c.status === "active" && (
                          <Button size="sm" variant="outline" onClick={() => updateStatus(c.campaign_id, "paused")} data-testid={`pause-${c.campaign_id}`}>
                            <Pause className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`edit-${c.campaign_id}`} title="Editar">
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => duplicate(c.campaign_id)} data-testid={`duplicate-${c.campaign_id}`} title="Duplicar">
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => deleteCampaign(c.campaign_id)} data-testid={`delete-${c.campaign_id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <AdsWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        addToCampaignId={wizardEditingId}
        onDone={() => { setWizardOpen(false); setWizardEditingId(null); load(); if (expandedId) toggleExpand(expandedId); }}
      />

      {/* Edit campaign dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="edit-campaign-dialog">
          <DialogHeader><DialogTitle>Editar campanha</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Nome</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="edit-camp-name" />
              </div>
              <div className="space-y-1">
                <Label>Anotações</Label>
                <Textarea rows={2} value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
              </div>
              <div className="flex items-center gap-3 p-3 border rounded-lg">
                <Switch checked={editing.cbo_enabled} onCheckedChange={(v) => setEditing({ ...editing, cbo_enabled: v })} />
                <span className="text-sm">Orçamento na campanha (CBO)</span>
              </div>
              {editing.cbo_enabled && (
                <div className="space-y-1">
                  <Label>Orçamento diário CBO (R$)</Label>
                  <Input type="number" min={5} value={editing.cbo_daily_budget}
                    onChange={(e) => setEditing({ ...editing, cbo_daily_budget: e.target.value })} />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={saveEdit} data-testid="edit-camp-save">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ label, value, icon, accent, small }) {
  return (
    <Card className={accent ? "border-emerald-500/40 bg-emerald-50/30 dark:bg-emerald-900/10" : ""}>
      <CardContent className={small ? "p-3" : "p-4"}>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{label}</span>
          <span className="text-muted-foreground">{icon}</span>
        </div>
        <div className={small ? "text-lg font-bold mt-1" : "text-2xl font-bold mt-1"} data-testid={`kpi-${label.toLowerCase().replace(/[^a-z]/g, "")}`}>{value}</div>
      </CardContent>
    </Card>
  );
}

function MiniChart({ data }) {
  const max = Math.max(1, ...data.map((d) => d.impressions));
  return (
    <div className="border rounded-lg p-3" data-testid="mini-chart">
      <p className="text-xs font-semibold mb-2">Impressões nos últimos 7 dias</p>
      <div className="flex items-end gap-1 h-32">
        {data.map((d) => (
          <div key={d.day} className="flex-1 flex flex-col items-center gap-1" title={`${d.day}: ${d.impressions} impressões`}>
            <div className="w-full bg-primary/70 rounded-t" style={{ height: `${(d.impressions / max) * 100}%`, minHeight: d.impressions > 0 ? "4px" : "0" }} />
            <span className="text-[9px] text-muted-foreground">{d.day.slice(5)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
